import React from 'react'
import { ValueLocked } from 'src/components/framework/icons/value-locked'
import { SectionHeader } from 'src/components/framework/ui/section-header'
import { Spotlight } from 'src/components/framework/ui/spotlight'
import {
  RatioLink,
  RatioRow,
  RatioTableData,
  buildRatioRows,
} from 'src/components/page/ratios/ratios'
import { useFeaturesContext } from 'src/contexts/features.context'
import { useSecurityContext } from 'src/contexts/security.context'
import {
  MomentumPriceVsHigh,
  MomentumRelativeStrength,
  MomentumVolumeChange,
} from 'src/shared/momentum-ratio'

export const MomentumRatios: React.FC = () => {
  const { features } = useFeaturesContext()
  const { security } = useSecurityContext()

  const dataSource = security.ratios.momentum
  const featureFlags = features.ratios.momentum

  const data: MomentumTableData = [
    {
      label: 'Relative strength (%)',
      rows: buildRatioRows(
        RELATIVE_STRENGTH_RATIOS,
        featureFlags.relativeStrength,
        dataSource.relativeStrength,
      ),
    },
    {
      label: 'Volume change (%)',
      rows: buildRatioRows(
        VOLUME_CHANGE_RATIOS,
        featureFlags.volumeChange,
        dataSource.volumeChange,
      ),
    },
    {
      label: 'Price vs... (%)',
      rows: buildRatioRows(
        PRICE_VS_HIGH_RATIOS,
        featureFlags.priceVsHigh,
        dataSource.priceVsHigh,
      ),
    },
  ]

  return (
    <>
      <SectionHeader>Momentum</SectionHeader>
      <div className={'stk-table-group stk-space-out-t-0'}>
        {data.map((table, tableIndex) => (
          <table key={tableIndex}>
            <thead>
              <tr>
                <th colSpan={2}>{table.label}</th>
              </tr>
            </thead>

            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>
                    <RatioLink id={row.ratioId}>{row.label}</RatioLink>
                  </td>
                  {row.enabled ? (
                    <td className={'stk-text-align-r'}>
                      {row.data ? <Spotlight data={row.data} /> : 'n/a'}
                    </td>
                  ) : (
                    <td className={'stk-text-align-r'}>
                      <ValueLocked />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </>
  )
}

const RELATIVE_STRENGTH_RATIOS: ReadonlyArray<
  RatioRow<MomentumRelativeStrength>
> = [
  {
    id: '1m',
    label: '1m',
    ratioId: 'relative-strength-1-month-766',
    enabled: false,
  },
  {
    id: '3m',
    label: '3m',
    ratioId: 'relative-strength-3-months-768',
    enabled: false,
  },
  {
    id: '6m',
    label: '6m',
    ratioId: 'relative-strength-6-months-769',
    enabled: false,
  },
  {
    id: '1yr',
    label: '1yr',
    ratioId: 'relative-strength-1-year-767',
    enabled: false,
  },
]

const VOLUME_CHANGE_RATIOS: ReadonlyArray<RatioRow<MomentumVolumeChange>> = [
  {
    id: '10d/3m',
    label: '10d/3m',
    ratioId: '10-day-vs-3-month-average-volume-5009',
    enabled: false,
  },
]

const PRICE_VS_HIGH_RATIOS: ReadonlyArray<RatioRow<MomentumPriceVsHigh>> = [
  {
    id: '52w',
    label: '52w High',
    ratioId: 'price-vs-52-week-high-3',
    enabled: false,
  },
  {
    id: '50dMA',
    label: '50d MA',
    ratioId: 'price-vs-50-day-moving-average-2',
    enabled: false,
  },
  {
    id: '200dMA',
    label: '200d MA',
    ratioId: 'price-vs-200-day-moving-average-1',
    enabled: false,
  },
]

type MomentumTableData = RatioTableData<
  RatioRow<
    MomentumRelativeStrength | MomentumVolumeChange | MomentumPriceVsHigh
  >
>[]
